import { CrudDecorator, DataHeaderProps, DataSourceMode, DropdownItem, EditRowDecorator, Label, Layout, TableRow, TableRowDisplayEvent, Textbox, ClickEvent, TableRowMode } from "@mcleod/components";
import { AutogenLayoutEdiEventStatusTable } from "./autogen/AutogenLayoutEdiEventStatusTable";
import { Api, ModelRow } from "@mcleod/core";
import { CrudDecoratorProps } from "@mcleod/components/src/page/decorators/CrudDecoratorProps";
import { EdiEventStatusTableEdit } from "./EdiEventStatusTableEdit";

export class EdiEventStatusTable extends AutogenLayoutEdiEventStatusTable {
    private static readonly REQUIRED: string = "Y";
    private static readonly OPTIONAL: string = "O";
    private _profileId: string;
    private _version: string;
    private _direction: string;
    private _partnerId: string;
    private _altPartnerId: string;

    override async onLoad() {
        this.tableEdiStatusEvent.crudManager.showCrud = (mode: DataSourceMode, props?: Partial<CrudDecoratorProps>, dataHeaderProps?: Partial<DataHeaderProps>) => {
            const layout = Layout.getLayout(this.tableEdiStatusEvent.addLayout) as EdiEventStatusTableEdit;
            layout.partnerId = this.partnerId;
            layout.version = this.version;
            layout.direction = this.direction;
            layout.altPartnerId = this.altPartnerId;
            layout.isLoadingData = true;

            const editingRowId = (this.tableEdiStatusEvent.selectedRow?.data as ModelRow)?.get("id");
            let savedRequiredFields: ModelRow[] | null = null;
            let savedCreationRules: ModelRow[] | null = null;
            layout.addLayoutLoadListener(() => {
                const existingBeforePost = layout.mainDataSource.doBeforeDataSourcePost;
                layout.mainDataSource.doBeforeDataSourcePost = async (): Promise<boolean> => {
                    savedRequiredFields = [...(layout.tableRequiredField?.data ?? [])];
                    savedCreationRules = [...(layout.tableReqdFldGrp?.data ?? [])];
                    if (existingBeforePost) {
                        return existingBeforePost();
                    }
                    return true;
                };
            });

            const crudDecorator = new CrudDecorator({
                layout: layout,
                mode: mode,
                headerProps: {
                    showClose: true,
                    showSaveAndClose: true,
                    title: mode === DataSourceMode.ADD ? "DataFusion Shipment Status Event - New" : "DataFusion Shipment Status Event - Edit",
                    doAfterClose: () => { },
                    ...dataHeaderProps,
                },
                doAfterSlideOut: async () => {
                    // Only update the parent row if the user actually saved (doBeforeDataSourcePost sets savedRequiredFields)
                    if (savedRequiredFields == null) {
                        return;
                    }
                    const requiredFields = savedRequiredFields;
                    const creationRules = savedCreationRules ?? layout.tableReqdFldGrp?.data;
                    const editingRow = this.sourceEdistatuseventTable?.data?.find((r: ModelRow) => r.get("id") === editingRowId);
                    editingRow?.set("required_fields", requiredFields);
                    editingRow?.set("creation_rules", creationRules);
                    this.sourceEdistatuseventTable.notifyHasChangedComponents(true);

                    const tableRow = this.tableEdiStatusEvent.allRows.find(
                        (tr: TableRow) => (tr.data as ModelRow)?.get("id") === editingRowId
                    );
                    if (tableRow) {
                        const textboxStatusRequired = tableRow.findComponentById("textboxStatusRequired") as Textbox;
                        const labelConditioned = tableRow.findComponentById("labelConditioned") as Label;
                        if (textboxStatusRequired && labelConditioned) {
                            const statusRequired = editingRow?.get("status_required");
                            const creationRuleCount = creationRules?.length ?? 0;
                            labelConditioned.visible = creationRuleCount > 0
                                && (statusRequired === EdiEventStatusTable.REQUIRED || statusRequired === EdiEventStatusTable.OPTIONAL);
                        }
                    }
                },
                ...props
            });
            crudDecorator.slideIn({ speed: 200 });
        }

        this.tableEdiStatusEvent.onEditLayoutLoaded = (decorator: EditRowDecorator, tableRow: any) => {
            decorator.multiButton.dropdownItems = null;
            const ediEventStatusTableEdit = decorator.layout as EdiEventStatusTableEdit;
            ediEventStatusTableEdit.version = this.mainDataSource?.activeRow?.get("version");
            ediEventStatusTableEdit.partnerId = this.mainDataSource?.activeRow?.get("partner_id");
            ediEventStatusTableEdit.altPartnerId = this.mainDataSource?.activeRow?.get("alt_partner_id");
            ediEventStatusTableEdit.direction = this.mainDataSource?.activeRow?.get("direction");
            const superOnSave = decorator.onSave;
            decorator.onSave = async (updatedRow: any) => {
                const validateResult = ediEventStatusTableEdit?.mainDataSource.validate();
                if (validateResult) {
                    const rules = ediEventStatusTableEdit?.tableRequiredField?.data;
                    updatedRow.set('required_fields', rules);
                    const creationRules = ediEventStatusTableEdit?.tableReqdFldGrp?.data;
                    updatedRow.set('creation_rules', creationRules);
                    const validation = layout.layoutEventSchedule.validateScheduleFields();
                    if (validation.hasCheckedBoxes) {
                        layout.layoutEventSchedule.sourceEdischedule.activeRow?.set("parent_row_id", tableRow.data?.get("id"));
                        layout.layoutEventSchedule.sourceEdischedule.activeRow?.set("parent_row_type", "E");
                        layout.layoutEventSchedule.sourceEdischedule.activeRow?.set("partner_id", this.mainDataSource?.activeRow?.get("partner_id"));
                        layout.layoutEventSchedule.sourceEdischedule.activeRow?.set("alt_partner_id", this.mainDataSource?.activeRow?.get("alt_partner_id"));
                        layout.layoutEventSchedule.sourceEdischedule.activeRow?.set("version", this.mainDataSource?.activeRow?.get("version"));
                        await layout.layoutEventSchedule.sourceEdischedule.post();
                    }
                    await superOnSave(updatedRow);
                }
            };
            const layout = decorator.layout as EdiEventStatusTableEdit;
        };
    }

    onRowDisplay(event: TableRowDisplayEvent) {
        const tableRow: TableRow = event.getTableRow();
        const rowData: ModelRow = tableRow.data;
        const textboxStatusRequired = tableRow.findComponentById("textboxStatusRequired") as Textbox;
        const labelConditioned = tableRow.findComponentById("labelConditioned") as Label;
        const textboxEventType = tableRow.findComponentById("textboxEventType") as Textbox;

        // Handle event type display based on direction
        if (textboxEventType) {
            const direction = rowData?.get("direction");
            const eventTypeValue = rowData.get("event_type");

            // Set up dropdown items based on direction
            if (direction === "I") { // INBOUND
                textboxEventType.items = [
                    new DropdownItem("AS", "Arrive stop"),
                    new DropdownItem("DS", "Depart stop"),
                    new DropdownItem("LC", "Position"),
                    new DropdownItem("ET", "ETA"),
                    new DropdownItem("IN", "Informational")
                ];
            } else if (direction === "O") { // OUTBOUND
                textboxEventType.items = [
                    new DropdownItem("AP", "Appointment"),
                    new DropdownItem("ST", "Stop"),
                    new DropdownItem("LC", "Position"),
                    new DropdownItem("ET", "ETA"),
                    new DropdownItem("OH", "Order history")
                ];
            }

            // Set the selected item to show display value
            if (eventTypeValue && Array.isArray(textboxEventType.items)) {
                const selectedItem = textboxEventType.items.find(item =>
                    typeof item === 'object' && item.value === eventTypeValue
                ) as DropdownItem;
                if (selectedItem) {
                    textboxEventType.selectedItem = selectedItem;
                }
            }
        }

        if (textboxStatusRequired
            && labelConditioned
            && !textboxStatusRequired.text.endsWith("/Conditioned")
            && rowData.getNumber("edi_reqdfldgroup_count", 0) > 0
            && (rowData.get("status_required") === EdiEventStatusTable.REQUIRED || rowData.get("status_required") === EdiEventStatusTable.OPTIONAL)) {
            labelConditioned.visible = true;
        }
    }

    /** This is an event handler for the onClick event of buttonDefaults. */
    buttonDefaultsOnClick(event: ClickEvent) {
        this.buttonDefaults.busy = true;
        const allRows = this.tableEdiStatusEvent.allRows;
        for (let i = allRows.length - 1; i >= 0; i--) {
            this.tableEdiStatusEvent.deleteRow(allRows[i].index);
            //need to delete child rows too?look at using doBeforePost or doAfterPost on datasource...set a member variable
        }

        //Generate default rows
        Api.post("lme/datafusion/generate-status-event-defaults", { profile_id: this._profileId }).then((response: any) => {
            const defaultRows = response.data;
            for (const rowData of defaultRows) {
                const tableRow = new ModelRow(this.sourceEdistatuseventTable.url, false, rowData);
                tableRow._appending = true;
                const addRowResult = this.tableEdiStatusEvent.addRow(tableRow, { mode: TableRowMode.ADD }, { display: true, addToData: true });
                if (addRowResult.row) {
                    // Force the row to display its data properly
                    addRowResult.row.displayData(tableRow, this.sourceEdistatuseventTable.data, addRowResult.row.index);
                }
            }
            this.buttonDefaults.busy = false;
        });

    }

    public get profileId(): string {
        return this._profileId;
    }
    public set profileId(value: string) {
        this._profileId = value;
    }

    public get version(): string {
        return this._version;
    }
    public set version(value: string) {
        this._version = value;
    }

    public get direction(): string {
        return this._direction;
    }
    public set direction(value: string) {
        this._direction = value;
    }

    public get partnerId(): string {
        return this._partnerId;
    }
    public set partnerId(value: string) {
        this._partnerId = value;
    }

    public get altPartnerId(): string {
        return this._altPartnerId;
    }
    public set altPartnerId(value: string) {
        this._altPartnerId = value;
    }
}
